/** One drawer in the stack's URL: a kind, then its id if that kind takes one. */
export interface DetailStackEntry<K extends string = string> {
  kind: K;
  /** null for a kind that takes no id. */
  id: string | null;
}

/** Which kinds there are, and whether each takes an id. */
export type DetailStackKindArity<K extends string> = Record<K, boolean>;

/**
 * Parses the URL after a page's path into drawers, bottom to top:
 * "orders/A/settings/customers/C" → [{ orders, A }, { settings }, { customers, C }].
 * After a kind with an id the next segment is its id. Stops at the first unknown kind (or a kind
 * missing its id), ignoring a mangled tail.
 */
export const toDetailStackEntryList = <K extends string>(
  splat: string,
  hasIdByKind: DetailStackKindArity<K>,
): DetailStackEntry<K>[] => {
  const kinds = Object.keys(hasIdByKind) as K[];
  const segments = splat.split("/").filter((segment) => segment !== "");
  const entries: DetailStackEntry<K>[] = [];
  let index = 0;
  while (index < segments.length) {
    const kind = kinds.find((known) => known === segments[index]);
    if (!kind) break;
    if (!hasIdByKind[kind]) {
      entries.push({ kind, id: null });
      index += 1;
      continue;
    }
    if (index + 1 >= segments.length) break;
    entries.push({ kind, id: decodeURIComponent(segments[index + 1]) });
    index += 2;
  }
  return entries;
};

/** The page's path plus the given drawers, as a URL path. */
export const toDetailStackPath = (base: string, entries: DetailStackEntry[]): string => {
  const tail = entries
    .map((entry) => {
      if (entry.id === null) {
        return `/${entry.kind}`;
      }
      return `/${entry.kind}/${encodeURIComponent(entry.id)}`;
    })
    .join("");
  return `${base}${tail}`;
};

/** How wide a drawer is: `wide` for viewing a record, `compact` for creating or changing something. */
export type DetailStackSize = "wide" | "compact";

/** A drawer's width for its size, capped by the window. */
export const DETAIL_STACK_WIDTH: Record<DetailStackSize, string> = {
  wide: "min(960px, 92vw)",
  compact: "min(480px, 100vw)",
};
